import { formatPercent } from "@/utils/money";
import type {
  ArranjoFaturamento,
  OmieEmpresaChave,
  PatchAgenciaDaPropostaBody,
  PropostaAgenciaDetail,
  PropostaStatus,
} from "@/types/finance";
import { propostaEhEditavel } from "@/utils/proposta-status";

export type CampoDoCadastro = keyof PatchAgenciaDaPropostaBody;

/** Os campos de texto guardam o que foi digitado; viram número ou null ao salvar. */
export interface RascunhoDoCadastro {
  sigla: string;
  razao_social: string;
  cnpj: string;
  endereco: string;
  contato_nome: string;
  contato_email: string;
  contato_cargo: string;
  fatura_por_competencia_mensal: boolean;
  dia_emissao_competencia: string;
  prazo_pagamento_competencia_dias: string;
  impostos_percentual: string;
  faturamento_empresa: OmieEmpresaChave | null;
  faturamento_arranjo: ArranjoFaturamento | null;
}

const CAMPOS_DE_TEXTO = [
  "sigla",
  "razao_social",
  "cnpj",
  "endereco",
  "contato_nome",
  "contato_email",
  "contato_cargo",
] as const;

/** Impressos nos documentos ou usados nas contas de toda proposta que segue a agência. */
const CAMPOS_QUE_ALCANCAM_OUTRAS_PROPOSTAS: ReadonlySet<CampoDoCadastro> = new Set<CampoDoCadastro>([
  "razao_social",
  "cnpj",
  "endereco",
  "fatura_por_competencia_mensal",
  "dia_emissao_competencia",
  "prazo_pagamento_competencia_dias",
  "impostos_percentual",
]);

/** Same as SIGLA_PATTERN in the API. */
const PADRAO_DA_SIGLA = /^[A-Z0-9]{2,6}( [A-Z0-9]{2,6})?$/;
const PADRAO_DO_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function idDoCampoNoCadastro(campo: CampoDoCadastro): string {
  return `cadastro-agencia-${campo}`;
}

export function rascunhoDoCadastro(agencia: PropostaAgenciaDetail): RascunhoDoCadastro {
  return {
    sigla: agencia.sigla ?? "",
    razao_social: agencia.razao_social ?? "",
    cnpj: agencia.cnpj ?? "",
    endereco: agencia.endereco ?? "",
    contato_nome: agencia.contato_nome ?? "",
    contato_email: agencia.contato_email ?? "",
    contato_cargo: agencia.contato_cargo ?? "",
    fatura_por_competencia_mensal: agencia.fatura_por_competencia_mensal,
    dia_emissao_competencia: agencia.dia_emissao_competencia != null ? String(agencia.dia_emissao_competencia) : "",
    prazo_pagamento_competencia_dias:
      agencia.prazo_pagamento_competencia_dias != null ? String(agencia.prazo_pagamento_competencia_dias) : "",
    impostos_percentual: agencia.impostos_percentual ? formatPercent(agencia.impostos_percentual) : "",
    faturamento_empresa: agencia.faturamento_empresa,
    faturamento_arranjo: agencia.faturamento_arranjo,
  };
}

function inteiroNoIntervalo(texto: string, minimo: number, maximo: number): boolean {
  const numero = Number(texto.trim());
  return Number.isInteger(numero) && numero >= minimo && numero <= maximo;
}

function percentualDoTexto(texto: string): number {
  return Number(texto.trim().replace(",", "."));
}

export function errosDoRascunho(rascunho: RascunhoDoCadastro): Partial<Record<CampoDoCadastro, string>> {
  const erros: Partial<Record<CampoDoCadastro, string>> = {};
  const sigla = rascunho.sigla.trim().replace(/\s+/g, " ").toUpperCase();
  if (sigla && !PADRAO_DA_SIGLA.test(sigla)) erros.sigla = "Use de 2 a 6 letras ou números (ex.: MTO, ALMP ENY)";
  if (rascunho.contato_email.trim() && !PADRAO_DO_EMAIL.test(rascunho.contato_email.trim())) {
    erros.contato_email = "E-mail inválido";
  }
  if (rascunho.dia_emissao_competencia.trim() && !inteiroNoIntervalo(rascunho.dia_emissao_competencia, 1, 31)) {
    erros.dia_emissao_competencia = "Use um dia de 1 a 31";
  }
  if (
    rascunho.prazo_pagamento_competencia_dias.trim() &&
    !inteiroNoIntervalo(rascunho.prazo_pagamento_competencia_dias, 0, 365)
  ) {
    erros.prazo_pagamento_competencia_dias = "Use de 0 a 365 dias";
  }
  if (rascunho.impostos_percentual.trim()) {
    const percentual = percentualDoTexto(rascunho.impostos_percentual);
    if (!Number.isFinite(percentual) || percentual < 0 || percentual > 100) {
      erros.impostos_percentual = "Use um percentual de 0 a 100";
    }
  }
  return erros;
}

/** Só o que mudou em relação ao cadastro atual. Espera um rascunho sem erros. */
export function patchDoRascunho(
  rascunho: RascunhoDoCadastro,
  agencia: PropostaAgenciaDetail,
): PatchAgenciaDaPropostaBody {
  const original = rascunhoDoCadastro(agencia);
  const patch: PatchAgenciaDaPropostaBody = {};
  for (const campo of CAMPOS_DE_TEXTO) {
    if (rascunho[campo].trim() !== original[campo].trim()) patch[campo] = rascunho[campo].trim() || null;
  }
  if (rascunho.fatura_por_competencia_mensal !== original.fatura_por_competencia_mensal) {
    patch.fatura_por_competencia_mensal = rascunho.fatura_por_competencia_mensal;
  }
  if (rascunho.dia_emissao_competencia.trim() !== original.dia_emissao_competencia) {
    patch.dia_emissao_competencia = rascunho.dia_emissao_competencia.trim()
      ? Number(rascunho.dia_emissao_competencia.trim())
      : null;
  }
  if (rascunho.prazo_pagamento_competencia_dias.trim() !== original.prazo_pagamento_competencia_dias) {
    patch.prazo_pagamento_competencia_dias = rascunho.prazo_pagamento_competencia_dias.trim()
      ? Number(rascunho.prazo_pagamento_competencia_dias.trim())
      : null;
  }
  if (rascunho.impostos_percentual.trim() !== original.impostos_percentual) {
    patch.impostos_percentual = percentualDoTexto(rascunho.impostos_percentual) || null;
  }
  if (
    rascunho.faturamento_empresa !== original.faturamento_empresa ||
    rascunho.faturamento_arranjo !== original.faturamento_arranjo
  ) {
    patch.faturamento_empresa = rascunho.faturamento_empresa;
    patch.faturamento_arranjo = rascunho.faturamento_empresa ? rascunho.faturamento_arranjo : null;
  }
  return patch;
}

export function alcancaOutrasPropostas(patch: PatchAgenciaDaPropostaBody): boolean {
  return (Object.keys(patch) as CampoDoCadastro[]).some((campo) => CAMPOS_QUE_ALCANCAM_OUTRAS_PROPOSTAS.has(campo));
}

export function outrasPropostasAbertas<
  T extends { id: string; status: PropostaStatus },
>(propostas: T[], propostaAtualId: string): T[] {
  return propostas.filter((proposta) => proposta.id !== propostaAtualId && propostaEhEditavel(proposta.status, false));
}

/** "Razão social (nome)": mesma regra de agency_identification na API. */
export function identificacaoDaAgencia(razaoSocial: string | null, nome: string): string {
  if (razaoSocial && nome && nome.trim().toLowerCase() !== razaoSocial.trim().toLowerCase()) {
    return `${razaoSocial} (${nome})`;
  }
  return razaoSocial || nome;
}
